import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Page from "@/components/Page";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ContactBanner from "@/components/ContactBanner";
import EditorialMarquee from "@/components/Marquee";
import { useContent, imgSrc } from "@/content/ContentContext";

const About = () => {
  const { content } = useContent();
  const { about } = content;
  return (
    <Page data-testid="about-page">
      <PageHero
        id="about"
        overline={about.heroOverline}
        lines={about.heroLines}
        intro={about.heroIntro}
      />

      <section className="bg-cream" data-testid="about-chapters">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal className="lg:sticky lg:top-32">
                <div className="border border-line bg-white p-4">
                  <img
                    src={imgSrc(about.logoImage)}
                    alt="Kevane Consulting & Training — brand identity"
                    className="w-full h-auto"
                    loading="lazy"
                    data-testid="about-brand-logo"
                  />
                </div>
                <p className="mt-4 text-xs tracking-[0.25em] uppercase text-ink/40">
                  {about.logoCaption}
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-7 space-y-0">
              {about.chapters.map((c, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <article className="border-t border-line py-12 first:pt-0 first:border-t-0" data-testid={`about-chapter-${i + 1}`}>
                    <div className="flex items-baseline gap-6">
                      <span className="font-serif text-5xl text-gold-muted/60 font-medium shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h2 className="font-serif text-3xl sm:text-4xl font-medium text-pine">{c.title}</h2>
                        <p className="mt-5 text-base leading-relaxed text-ink/70">{c.body}</p>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-line" data-testid="about-principles">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28 grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted mb-5">{about.principlesOverline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium text-pine leading-tight">
              {about.principlesTitle1}<br />{about.principlesTitle2}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink/70 max-w-md">
              {about.principlesText}
            </p>
          </Reveal>
          <div className="space-y-px bg-line border border-line">
            {about.principles.map((p, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div className="flex items-start gap-4 bg-white px-7 py-6" data-testid={`about-principle-${i + 1}`}>
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pine text-gold">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  <p className="text-sm sm:text-base text-ink/80 leading-relaxed">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream" data-testid="about-cta-strip">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-pine leading-snug max-w-xl">
              {about.ctaTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/services"
              data-testid="about-cta-services-link"
              className="group inline-flex items-center gap-2 rounded-full border border-pine/25 px-7 py-3.5 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-pine hover:text-cream"
            >
              {about.ctaCta}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee />
      <ContactBanner overline={about.banner.overline} title={about.banner.title} blurb={about.banner.blurb} />
    </Page>
  );
};

export default About;
